<!-- @format -->
<template>
    <div class="mdf-submit-pop-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Submit POP</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="loading" />
                <div v-else>
                    <div class="input-container">
                        <label>Vendor</label>
                        <div>{{ mdfSubmitPop.vendorName }}</div>
                    </div>
                    <div class="input-container">
                        <label>Fund title</label>
                        <div>{{ mdfSubmitPop.fundTitle }}</div>
                    </div>
                    <div class="pop-divider" />
                    <div
                        v-for="(allocatedFund, index) in mdfSubmitPop.allocatedFunds"
                        :key="index"
                        class="allocated-fund"
                    >
                        <pop-card
                            :item="allocatedFund.cardDetails"
                            :error="
                                $v.mdfSubmitPop.allocatedFunds.$each[index].$dirty &&
                                $v.mdfSubmitPop.allocatedFunds.$each[index].$invalid
                            "
                        ></pop-card>
                        <label>Notes for Vendor</label>
                        <app-textarea
                            v-model="allocatedFund.notesForVendor"
                            :auto-size="false"
                            class="vendor-notes"
                            :resize="false"
                            placeholder="Write a note"
                        >
                        </app-textarea>
                        <div class="vendor-notes-information-section">
                            <p>Optional</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-pop-submit back-button" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button class="send-pop-submit" :loading="loading" @click="submitPop"
                        >Submit POP</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const MDFPopCard = () => import('@/components/sliding-widgets/mdf/submit-pop/pop-card');
const AppTextArea = () => import('@/components/common/app-textarea');
import { required } from 'vuelidate/lib/validators';
import EventBus from '@/scripts/event-bus.js';
import { MDFClientRequestService } from '@/scripts/services/mdf/client/client-request-service';

export default {
    name: 'mdf-submit-pop-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'pop-card': MDFPopCard,
        'app-textarea': AppTextArea,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            mdfSubmitPop: {
                vendorName: '',
                fundTitle: '',
                allocatedFunds: [],
            },

            mdfRequestId: this.widget.mdfRequestId,
            accountClientId: this.widget.accountClientId,
            loadingFiles: false,
            isEdit: this.widget.edit,
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },
    },

    validations() {
        return {
            mdfSubmitPop: {
                allocatedFunds: {
                    $each: {
                        cardDetails: {
                            documentFiles: {
                                ...(this.mdfSubmitPop.proofRequired ? { required } : false),
                            },
                        },
                    },
                },
            },
        };
    },

    created() {
        this.getMDFRequestDetails();
    },

    methods: {
        mapAllocatedFunds(allocations, currencySymbol, currencyCode) {
            return allocations.map((allocation) => ({
                cardDetails: {
                    AllocatedFundName: allocation.Name,
                    fundAmount: {
                        currencyCode: currencyCode,
                        currency: currencySymbol,
                        amount: allocation.AmountSelectedCurrency,
                    },

                    documentPOPNeeded: allocation.ProofTypes,
                    documentFiles: this.mapDocumentFiles(allocation.FundAllocationPops),
                },

                allocationId: allocation.Id,
                notesForVendor: allocation.NotesForVendor,
            }));
        },

        mapDocumentFiles(Files) {
            return Files.map((file) => ({
                name: file.Name,
                url: file.Url,
                size: file.Size,
                type: file.Type,
                fileId: file.FileId,
                id: file.Id,
            }));
        },

        getMDFRequestDetails() {
            this.loading = true;
            MDFClientRequestService.getRequestById(this.mdfRequestId, this.accountClientId)
                .then(({ data: { data } }) => {
                    this.mdfSubmitPop = {
                        vendorName: data.AgencyName,
                        fundTitle: data.Name,
                        allocatedFunds: this.mapAllocatedFunds(
                            data.Allocations,
                            data.CurrencySymbol,
                            data.CurrencyCode,
                        ),
                        proofRequired: data.ProofRequired,
                    };
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        submitPop() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.loading = true;
            this.uploadAllInCollaterals().then(() => {
                const payload = {
                    Allocations: this.mdfSubmitPop.allocatedFunds.map((card) => {
                        const allocationItem = {
                            Id: card.allocationId,
                            Proofs: this.mapFiles(card.cardDetails.documentFiles),
                        };
                        if (card.notesForVendor) {
                            allocationItem.NotesForVendor = card.notesForVendor;
                        }
                        return allocationItem;
                    }),
                };

                if (this.isEdit) {
                    this.editPop(payload);
                } else {
                    this.postPop(payload);
                }
            });
        },

        postPop(payload) {
            MDFClientRequestService.submitPop(this.mdfRequestId, this.accountClientId, payload)
                .then(() => {
                    EventBus.$emit('pop-submitted:update');
                    this.$helpers.showSuccessNotification("You've successfully uploaded your POP.");
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while sending the POP.', {
                        error,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        editPop(payload) {
            MDFClientRequestService.editPop(this.mdfRequestId, this.accountClientId, payload)
                .then(() => {
                    EventBus.$emit('pop-submitted:update');
                    this.$helpers.showSuccessNotification("You've successfully edited your POP.");
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating the POP.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        uploadAllInCollaterals() {
            this.loadingFiles = true;

            return new Promise((resolve, reject) => {
                const valuesRequests = [];
                this.mdfSubmitPop.allocatedFunds.forEach((allocateFund) => {
                    allocateFund.cardDetails.documentFiles.forEach((documentFile) => {
                        const promise = this.uploadInCollaterals(documentFile);
                        if (promise) {
                            valuesRequests.push(promise);
                        }
                    });
                });
                Promise.all(valuesRequests)
                    .then((values) => {
                        resolve(values);
                    })
                    .catch(reject)
                    .finally(() => {
                        this.loadingFiles = false;
                    });
            });
        },

        uploadInCollaterals(documentFile) {
            if (documentFile.fileId) {
                return;
            }
            return new Promise((resolve, reject) => {
                this.mediaImageRequest(documentFile.name, documentFile.file)
                    .then((result) => {
                        documentFile.fileId = result.Id;
                        documentFile.url = result.Url;
                        documentFile.name = result.Name;
                        documentFile.size = result.FileSizeInBytes;
                        documentFile.type = result.MimeType;
                        documentFile.iconUrl = this.$helpers.getThumbnailUrlFromMimeType(
                            result.MimeType,
                        );

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your image. Please try again.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        mediaImageRequest(name, fileForSend) {
            return new Promise((resolve, reject) => {
                const formData = new FormData();
                formData.append('file', fileForSend, name);
                formData.append('clientId', this.clientId);
                formData.append('agencyId', this.agencyId);
                this.$api
                    .ajax({
                        url: '/api/Collaterals/File',
                        method: 'POST',
                        data: formData,
                        contentType: false,
                        processData: false,
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your attachment piece',
                            { error },
                        );
                        reject();
                    });
            });
        },

        mapFiles(items) {
            return items.map((file) => {
                const filePayload = {
                    Name: file.name,
                    Url: file.url,
                    Size: file.size,
                    Type: file.type,
                    FileId: file.fileId,
                };

                if (file.id) {
                    filePayload.Id = Number(file.id);
                }
                return filePayload;
            });
        },
    },
};
</script>

<style>
.mdf-submit-pop-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.mdf-submit-pop-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.mdf-submit-pop-widget .input-container {
    margin-bottom: 24px;
}

.mdf-submit-pop-widget .pop-divider {
    border-bottom: 1px solid #e9e9ef;
    margin: 24px 0;
}

.mdf-submit-pop-widget .allocated-fund:not(:last-child) {
    border-bottom: 1px solid #e9e9ef;
    margin: 24px 0;
}

.mdf-submit-pop-widget .vendor-notes textarea {
    background: #f7f7f8 0% 0% no-repeat padding-box;
    border: 1px solid #cac6cb;
}

.mdf-submit-pop-widget .vendor-notes-information-section {
    color: #877d88;
    font-size: 12px;
    margin: 25px 0;
}

.mdf-submit-pop-widget .app-button.cancel-pop-submit.back-button {
    width: 162px;
    height: 40px;
    border: 1px solid #cac6cb;
}

.mdf-submit-pop-widget .app-button.send-pop-submit {
    width: 152px;
    height: 40px;
}
</style>
